import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';

// Layouts
import MainLayout from '@/components/layout/MainLayout';
import AuthLayout from '@/components/layout/AuthLayout';

// Auth pages
import LoginPage from '@/features/auth/LoginPage';
import RegisterPage from '@/features/auth/RegisterPage';
import VerifyEmailPage from '@/features/auth/VerifyEmailPage';
import ForgotPasswordPage from '@/features/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/features/auth/ResetPasswordPage';

// Main pages
import Dashboard from '@/features/dashboard/Dashboard';
import LabelForge from '@/features/labels/LabelForge';
import ShopFather from '@/features/shop/ShopFather';
import PricingPage from '@/features/pricing/PricingPage';
import SettingsPage from '@/features/settings/SettingsPage';

// Public pages
import LandingPage from '@/features/landing/LandingPage';
import PublicForm from '@/features/forms/PublicForm';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Guest route wrapper (for login/register)
function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { accessToken, setUser, setLoading, logout } = useAuthStore();

  // Check auth on mount
  useEffect(() => {
    async function checkAuth() {
      if (!accessToken) {
        setLoading(false);
        return;
      }

      try {
        const { user } = await api.get<{ user: any }>('/auth/me');
        setUser(user);
      } catch {
        logout();
      } finally {
        setLoading(false);
      }
    }

    checkAuth();
  }, []);

  return (
    <Routes>
      {/* Public landing page - root */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Email verification */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      
      {/* Public forms */}
      <Route path="/forms/:slug" element={<PublicForm />} />

      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route
          path="/register"
          element={
            <GuestRoute>
              <RegisterPage />
            </GuestRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestRoute>
              <ForgotPasswordPage />
            </GuestRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <GuestRoute>
              <ResetPasswordPage />
            </GuestRoute>
          }
        />
      </Route>

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/labels/*" element={<LabelForge />} />
        <Route path="/shop/*" element={<ShopFather />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      {/* 404 - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

